import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

export type SitePhoto = {
  id: string;
  page: string;
  url: string;
  alt_text: string | null;
  display_order: number;
  target_asset: string | null;
};

/** Turns "/assets/hera-1-BxYz1234.jpg?x=1" into "hera-1" so dev and built filenames match. */
export function assetKey(src: string): string {
  const file = src.split('?')[0].split('/').pop() ?? src;
  const base = file.replace(/\.[^.]+$/, '');
  return base.replace(/-[A-Za-z0-9_-]{8,}$/, '').toLowerCase();
}

type Ctx = {
  photos: SitePhoto[];
  /** Returns the admin replacement for a bundled asset, or the original. */
  img: (src: string) => string;
  /** Extra photos an admin added to a page's gallery. */
  extras: (page: string) => SitePhoto[];
  /** Extra photos an admin added to a specific gallery section of a page. */
  sectionExtras: (page: string, section: string) => SitePhoto[];
  reload: () => void;
};

const AssetOverridesContext = createContext<Ctx>({
  photos: [],
  img: (s) => s,
  extras: () => [],
  sectionExtras: () => [],
  reload: () => {},
});

export function AssetOverridesProvider({ children }: { children: ReactNode }) {
  const [photos, setPhotos] = useState<SitePhoto[]>([]);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let active = true;
    supabase
      .from('photos')
      .select('id, page, url, alt_text, display_order, target_asset')
      .order('display_order')
      .then(({ data }) => {
        if (active) setPhotos((data as SitePhoto[]) ?? []);
      });
    return () => {
      active = false;
    };
  }, [tick]);

  const value = useMemo<Ctx>(() => {
    const map = new Map<string, string>();
    for (const p of photos) {
      if (p.target_asset) map.set(p.target_asset.toLowerCase(), p.url);
    }
    return {
      photos,
      img: (src: string) => map.get(assetKey(src)) ?? src,
      extras: (page: string) =>
        photos.filter((p) => !p.target_asset && p.page === page),
      sectionExtras: (page: string, section: string) =>
        photos.filter((p) => !p.target_asset && p.page === `${page}::${section}`),
      reload: () => setTick((t) => t + 1),
    };
  }, [photos]);

  return <AssetOverridesContext.Provider value={value}>{children}</AssetOverridesContext.Provider>;
}

export function useAssetOverrides() {
  return useContext(AssetOverridesContext);
}
